import {
    areSearchParamsEqual,
    STATUSES_ENCODED_QUERY_KEY,
    getHashSearchParams,
    unwrapEncodedSearchParams,
    wrapEncodedSearchParams,
} from './encodedUrlQuery';

export { areSearchParamsEqual };

export type StatusesFilterItem = { key: string; value: any };

export type StatusesUrlViewState = {
    hasAny: boolean;
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    archiveTableToggle: boolean;
    filterStatusesId: StatusesFilterItem | null;
    filterStatusName: StatusesFilterItem | null;
    filterStatusId: StatusesFilterItem | null;
    filterTypeId: StatusesFilterItem[] | null;
    uiFilters: Record<string, any>;
};

export type StatusesUrlWriteState = {
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    archiveTableToggle: boolean;
    filterStatusesId: StatusesFilterItem | null;
    filterStatusName: StatusesFilterItem | null;
    filterStatusId: StatusesFilterItem | null;
    filterTypeId: StatusesFilterItem[] | null;
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

export function readInitialStatusesUrlState(): StatusesUrlViewState {
    return parseStatusesUrlState(getHashSearchParams());
}

export function parseStatusesUrlState(params: URLSearchParams): StatusesUrlViewState {
    params = unwrapEncodedSearchParams(params, STATUSES_ENCODED_QUERY_KEY);
    const uiFilters: Record<string, any> = {};
    const page = parsePositiveInt(params.get('page'), DEFAULT_PAGE);
    const size = parsePositiveInt(params.get('size'), DEFAULT_SIZE);
    const orderBy = params.get('orderBy') || null;
    const isDesc = parseBooleanParam(params.get('desc'), true);
    const sortOrder = parseSortOrder(params.get('sortOrder'), orderBy, isDesc);
    const archiveTableToggle = !parseBooleanParam(params.get('archived'), false);

    const id = params.get('id');
    const name = params.get('name');
    const isActiveRaw = params.get('isActive');
    const isActive = isActiveRaw == null ? null : toTrueFalse(isActiveRaw);
    const typeId = getMultiParams(params, 'typeId').map(toNumberValue);

    if (id) uiFilters.id = id;
    if (name) uiFilters.statusName = name;
    if (isActive != null) {
        uiFilters.active = isActive
            ? {name: 'Активна', code: true}
            : {name: 'Неактивна', code: false};
    }
    if (typeId.length) uiFilters.typeId = typeId;

    return {
        hasAny: params.toString().length > 0,
        page,
        size,
        orderBy,
        isDesc,
        sortOrder,
        archiveTableToggle,
        filterStatusesId: toFilterItem('Id', id),
        filterStatusName: toFilterItem('StatusName', name),
        filterStatusId: isActive == null ? null : {key: 'Active', value: isActive},
        filterTypeId: toFilterList('TypeId', typeId),
        uiFilters,
    };
}

export function buildStatusesSearchParams(state: StatusesUrlWriteState): URLSearchParams {
    return wrapEncodedSearchParams(buildPlainStatusesSearchParams(state), STATUSES_ENCODED_QUERY_KEY);
}

function buildPlainStatusesSearchParams(state: StatusesUrlWriteState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.page && state.page !== DEFAULT_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.size && state.size !== DEFAULT_SIZE) {
        params.set('size', String(state.size));
    }
    if (state.orderBy && !(state.orderBy === 'Id' && state.sortOrder === 0)) {
        params.set('orderBy', state.orderBy);
        params.set('desc', state.isDesc ? '1' : '0');
        if (state.sortOrder) {
            params.set('sortOrder', String(state.sortOrder));
        }
    }
    if (!state.archiveTableToggle) {
        params.set('archived', '1');
    }

    setScalar(params, 'id', state.filterStatusesId?.value);
    setScalar(params, 'name', state.filterStatusName?.value);
    if (state.filterStatusId?.value !== undefined && state.filterStatusId?.value !== null && state.filterStatusId.value !== '') {
        params.set('isActive', normalizeBooleanParam(state.filterStatusId.value));
    }
    setMulti(params, 'typeId', state.filterTypeId);

    return params;
}

function setScalar(params: URLSearchParams, key: string, value?: string | null) {
    if (value) {
        params.set(key, value);
    }
}

function setMulti(params: URLSearchParams, key: string, items?: StatusesFilterItem[] | null) {
    items?.forEach((item) => {
        if (item?.value) {
            params.append(key, item.value);
        }
    });
}

function getMultiParams(params: URLSearchParams, key: string): string[] {
    const values: string[] = [];
    params.getAll(key).forEach((value) => {
        value.split(',').forEach((part) => {
            const trimmed = part.trim();
            if (trimmed) values.push(trimmed);
        });
    });
    return values;
}

function toFilterItem(key: string, value?: string | null): StatusesFilterItem | null {
    if (!value) return null;
    return { key, value };
}

function toFilterList(key: string, values?: Array<string | number>): StatusesFilterItem[] | null {
    if (!values?.length) return null;
    return values.map((value) => ({ key, value: String(value) }));
}

function toNumberValue(value: string): number | string {
    if (/^-?\d+$/.test(value)) {
        const parsed = Number(value);
        if (Number.isSafeInteger(parsed)) {
            return parsed;
        }
    }
    return value;
}

function toTrueFalse(value: string): boolean | null {
    if (value === '1' || value === 'true') return true;
    if (value === '0' || value === 'false') return false;
    return null;
}

function normalizeBooleanParam(value: any): string {
    if (value === true || value === 'true' || value === '1') return '1';
    if (value === false || value === 'false' || value === '0') return '0';
    if (value?.code === true) return '1';
    if (value?.code === false) return '0';
    return String(value);
}

function parsePositiveInt(value: string | null, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return fallback;
    }
    return parsed;
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
    if (value == null) return fallback;
    return value === '1' || value === 'true';
}

function parseSortOrder(value: string | null, orderBy: string | null, isDesc: boolean): number {
    if (value === '1' || value === '-1' || value === '0') {
        return Number(value);
    }
    if (!orderBy) return 0;
    return isDesc ? -1 : 1;
}
